import { Location, ViewportScroller } from "@angular/common";
import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize } from "rxjs/operators";
import { DynamicFormComponent } from "src/app/shared/components/dynamic-form/dynamic-form.component";
import { getUpdatedValues } from "src/app/shared/utils/helpers/common.helper";
import { AuthenticationService } from "../../../../core/authentication/authentication.service";
import { LoadingService } from "../../../../core/loader/loading.service";
import { DeleteModalComponent } from "../../../../shared/components/delete-modal/delete-modal.component";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { NotificationService } from "../../../../shared/components/notification/notification.service";
import { UserInfo } from "../../../../shared/types/user-info";
import { CreateTicketService } from "../create-ticket/create-ticket.service";
import { TicketService } from "../ticket.service";
import { Ticket } from "../ticket.type";

@Component({
  selector: "app-manage-tickets",
  templateUrl: "./manage-tickets.component.html",
  styleUrls: ["./manage-tickets.component.scss"],
  providers: [CreateTicketService],
})
export class ManageTicketsComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;
  @ViewChild("scrollToComments") scrollToComments: ElementRef;

  currentUser: UserInfo;
  ticketId: string;
  ticket: Ticket;
  questions: QuestionBase<string | boolean>[];
  showComments = false;
  isEdit: boolean;

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<DeleteModalComponent>,
    private route: ActivatedRoute,
    private router: Router,
    private loader: LoadingService,
    private questionService: CreateTicketService,
    private ticketService: TicketService,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    public location: Location,
    private scroller: ViewportScroller,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;
    this.loader.show();
  }

  ngOnInit(): void {
    this.ticketId = this.route.snapshot.paramMap.get("id");
    this.getTicket();
  }

  goBack(): void {
    this.location.back();
  }

  getTicket(): void {
    this.ticketService
      .getTicket(this.ticketId)
      .subscribe(
        (ticket: Ticket) => {
          this.ticket = ticket;
          this.buildForm(this.ticket);
        },
        (error) => {
          this.router.navigate(["/tickets"]);
          this.notificationService.error(error || "Couldn't find Ticket");
        },
      )
      .add(() => this.loader.hide());
  }

  buildForm(ticket?: Ticket): void {
    this.questionService
      .generateManageQuestions({ ticket, onboardingSkip: !this.currentUser.isGlobal() })
      .subscribe((qs) => {
        this.questions = qs;
      });
  }

  toggleCommentsView(): void {
    this.showComments = !this.showComments;
    if (!this.showComments) {
      this.scroller.scrollToAnchor("ticketView");
    }
  }

  scrollDown() {
    this.scrollToComments.nativeElement?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  //#region Handlers

  onToggleManageTicket = (): void => this.toggleManageTicket();

  onUpdateTicket = (): void => this.updateTicket();

  //#endregion

  private updateTicket = (): void => {
    this.loader.show();
    const body: any = getUpdatedValues(this.dynamicForm.form, this.ticket);
    this.ticketService
      .updateTicket(this.ticket.id, body)
      .pipe(finalize(() => this.dynamicForm.enable()))
      .subscribe(
        (ticket: Ticket) => {
          this.ticket = { ...this.ticket, ...ticket };
          this.toggleManageTicket();
          this.notificationService.success("Ticket updated");
          this.buildForm(ticket);
        },
        (error) => this.notificationService.error(error || "Could not update ticket"),
      )
      .add(() => this.loader.hide());
  };

  private toggleManageTicket = (): void => {
    this.isEdit = !this.isEdit;
    if (this.isEdit) this.scrollDown();
  };
}
